// #region Imports

import type { CssColor } from './CssColor';
import type { CssLengthUnit } from './CssLength';

// #endregion

/**
 * Represents a valid CSS box-shadow value (single or multiple).
 *
 * @public
 */
export type CssShadowSingle =
    | 'none'
    | `${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${CssColor}`
    | `inset ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${CssColor}`
    | `${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${CssColor}`
    | `inset ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${CssColor}`
    | `${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${CssColor}`
    | `inset ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${`${number}${Extract<CssLengthUnit, 'px'>}`} ${CssColor}`;

/**
 * Represents a valid CSS box-shadow value, including multiple shadows.
 *
 * @public
 */
export type CssShadow = CssShadowSingle | `${CssShadowSingle}, ${CssShadowSingle}` | `${CssShadowSingle}, ${CssShadowSingle}, ${CssShadowSingle}`;

/**
 * @public
 */
export namespace CssShadow {

    /**
     * Checks if the given value is a valid CSS box-shadow (single or multiple).
     *
     * @param value - The value to check.
     * @returns `true` if the value is a valid CSS box-shadow, otherwise `false`.
     */
    export function isCssShadow(value: unknown): value is CssShadow {
        if (typeof value !== 'string') {
            return false;
        }

        if (value.trim() === '') {
            return false;
        }

        if (value === 'none') {
            return true;
        }

        // Split multiple shadows by comma
        const shadows = value.split(/\s*,\s*/);

        return shadows.every(isCssShadowSingle);
    }

    /**
     * Checks if a single value is a valid CSS box-shadow (without multiple shadows).
     *
     * @param value - The value to check.
     * @returns `true` if the value is a valid single CSS box-shadow, otherwise `false`.
     */
    function isCssShadowSingle(value: string): boolean {
        const shadowRegex = new RegExp(
            '^' +
            '(inset\\s+)?' +                                  // Optional "inset"
            '(-?\\d+(\\.\\d+)?(px|em|rem|vh|vw|%)?)\\s+' +    // X-offset
            '(-?\\d+(\\.\\d+)?(px|em|rem|vh|vw|%)?)\\s+' +    // Y-offset
            '(-?\\d+(\\.\\d+)?(px|em|rem|vh|vw|%)?)?\\s*' +   // Optional Blur-radius
            '(-?\\d+(\\.\\d+)?(px|em|rem|vh|vw|%)?)?\\s*' +   // Optional Spread-radius
            '(rgba?\\(\\d{1,3},\\s*\\d{1,3},\\s*\\d{1,3}(,\\s*(0|1|0?\\.\\d+))?\\)|#[0-9a-fA-F]{3,6})$', // Color (RGB, RGBA, HEX)
            'i'
        );

        return shadowRegex.test(value);
    }
}
